import { AddressElements } from '../location';

export interface OktaAccount {
  id: string;
  created: string;
  activated: string;
  statusChanged: string;
  status: OktaStatus;
  profile: OktaProfile;
  lastUpdated: string;
  passwordChanged: string;
  lastLogin: string;
  _links: any;
}
export interface OktaProfile {
  firstName: string;
  lastName: string;
  email: string;
  login: string;
  mobilePhone: string;
}

export interface OktaCred {
  password: { value: string };
}

/**
 * All Okta account status
 *
 * Link : https://support.okta.com/help/s/article/58335656-What-are-the-different-user-statuses-in-the-Okta-Password-Health-Check-Report?language=en_US
 *
 *
 * PROVISIONED - New user is added in Okta but not activated yet
 * RECOVERY - Existing user, activated previously, is in password reset mode
 * ACTIVE - Active status
 * DEPROVISIONED - Deactivated in Okta
 * PASSWORD EXPIRED - User password is expired
 * STAGED - New users created through the API and not activated yet
 * **/
export type OktaStatus =
  | 'STAGED'
  | 'ACTIVE'
  | 'PASSWORD EXPIRED'
  | 'PROVISIONED'
  | 'RECOVERY'
  | 'DEPROVISIONED';

export interface AccessInfo {
  expiresAt: Date;
  dossierNumber?: string;
  login: string;
  serviceAccount?: string;
}

export interface PreDeclarationInfo {
  expiresAt: Date;
  plateNumber: string;
  login: string;
  insurerId: string;
  car?: PreDeclarationCarOption;
  customer?: PreDeclarationCustomerOption;
}

export interface PreDeclarationCarOption {
  manufacturer: string;
  model: string;
  version: string;
  firstRegistration?: Date;
}

export interface PreDeclarationCustomerOption {
  firstname: string;
  lastname: string;
  email: string;
  location: {
    address: string;
    addressElements?: AddressElements;
  };
  phoneNumber: string;
}

export interface OktaCreateUserData {
  profile: OktaProfile;
  credentials: OktaCred;
  groupIds: string[];
}

export interface OktaUpdateUserData {
  profile: OktaProfile;
}

export interface AccessTokenData {
  accessToken: {
    value: string;
    accessToken: string;
    claims: Record<string, any>;
    expiresAt: number;
    tokenType: 'Bearer';
    scopes: string[];
    userinfoUrl?: string;
    authorizeUrl?: string;
  };
  idToken: {
    expiresAt: number;
    authorizeUrl?: string;
    scopes: string[];
    idToken: string;
    claims: Record<string, any>;
    issuer: string;
    clientId: string;
  };
  refreshToken: string;
  dossierNumber?: string;
  login: string;
  userTypes?: string[];
}

export interface PreDeclarationTokenData {
  accessToken: {
    value: string;
    accessToken: string;
    claims: Record<string, any>;
    expiresAt: number;
    tokenType: 'Bearer';
    scopes: string[];
    userinfoUrl?: string;
    authorizeUrl?: string;
  };
  idToken: {
    expiresAt: number;
    authorizeUrl?: string;
    scopes: string[];
    idToken: string;
    claims: Record<string, any>;
    issuer: string;
    clientId: string;
  };
  refreshToken: string;
  plateNumber?: string;
  login: string;
  insurerId: string;
  userTypes?: string[];
  car?: PreDeclarationCarOption;
  customer?: PreDeclarationCustomerOption;
  dossierNumber?: string;
}

export const getOktaGroupFromEnv = (oktaGroup: string): string => {
  switch (oktaGroup) {
    case 'local':
      return 'grpEnvDev';
    case 'dev2':
      return 'grpEnvDev';
    case 'int2':
      return 'grpEnvInt';
    case 'rec':
      return 'grpEnvRec';
    case 'demo':
      return 'grpEnvDemo';
    case 'green':
      return 'grpEnvProd';
    case 'blue':
      return 'grpEnvProd';
    default:
      return oktaGroup;
  }
};

export interface OktaTokenResponse {
  token_type: string;
  expires_in: number;
  access_token: string;
  scope: string;
  id_token: string;
  refresh_token: string;
  userinfoUrl?: string;
  authorizeUrl?: string;
}

export interface OktaGroupData {
  id: string;
  profile: {
    name: string;
    description: string;
  };
}
